import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { CoolsetPageLoader } from "shared/components/Loader/CoolsetLoader";
import { CheckIcon } from "shared/icons/CheckIcon";
import { FieldIdPrefixes } from "../../helpers/blocks";
import { CopyAndPasteBlocksStore } from "../../hooks/useCopyAndPasteBlocks";
import { TablePastePreview } from "./TablePastePreview";
import { validate } from "shared/helpers/validate";

export const PasteBlocksDialog = (props: PasteBlocksDialogProps) => {
  const { copyAndPasteBlocksStore, enableExistingBlocks } = props;

  const {
    blocksToPaste,
    isPasteDialogOpen,
    isPasteConditions,
    setIsPasteConditions,
    isPasteRelatedSupportingInfo,
    setIsPasteRelatedSupportingInfo,
    cancelPaste,
    loaderText,
    isLoading,
    canCreate,
    findAndReplaceForAllFieldIds,
    handleNewFieldIdChange,
    handleNewChildFieldIdChange,
    handleIsPasteAsExistingChange,
    handleIsPasteAsExistingChildChange,
    removeBlockToPaste,
    blockError,
    createBlocks,
    isTablePaste,
    pasteAnchorRow,
    setPasteAnchorRow,
    pasteAnchorCol,
    setPasteAnchorCol,
    tableScale,
    positionInstructionsToPaste,
    copiedPositionInstructions,
  } = copyAndPasteBlocksStore;

  const [stringToFind, setStringToFind] = useState<string>("");
  const [stringToReplace, setStringToReplace] = useState<string>("");

  const getFieldIdError = (fieldId: string) => {
    return validate.getFieldIdError(fieldId, { validateEmpty: true });
  };

  return (
    <Dialog className="layout-paste-blocks-dialog" open={isPasteDialogOpen} maxWidth={false}>
      <CoolsetPageLoader isOpen={isLoading} text={loaderText} />

      <DialogTitle variant="h2">Paste blocks</DialogTitle>
      <DialogContent>
        <Typography variant="h4" mb={1} color="text.secondary">
          Paste options
        </Typography>
        <div className="module-paste-options">
          <FormControlLabel
            control={<Checkbox checked={isPasteConditions} />}
            label="Copy conditions"
            onChange={() => setIsPasteConditions(!isPasteConditions)}
          />
          <FormControlLabel
            control={<Checkbox checked={isPasteRelatedSupportingInfo} />}
            label="Copy related supporting info"
            onChange={() => setIsPasteRelatedSupportingInfo(!isPasteRelatedSupportingInfo)}
          />
        </div>

        {isTablePaste && (
          <>
            <Typography variant="h4" mb={1} color="text.secondary">
              Paste position
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Click a cell to set the top-left anchor, or type row/column directly. Blue = paste preview, grey =
              occupied, red = conflict.
            </Typography>
            <TablePastePreview
              tableScale={tableScale}
              positionInstructionsToPaste={positionInstructionsToPaste}
              copiedPositionInstructions={copiedPositionInstructions}
              pasteAnchorRow={pasteAnchorRow}
              pasteAnchorCol={pasteAnchorCol}
              setPasteAnchorRow={setPasteAnchorRow}
              setPasteAnchorCol={setPasteAnchorCol}
            />
            <div className="module-find-and-replace" style={{ marginTop: 12 }}>
              <TextField
                label="Start row"
                type="number"
                value={pasteAnchorRow}
                onChange={(e) => setPasteAnchorRow(Math.max(1, parseInt(e.target.value) || 1))}
                size="small"
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Start column"
                type="number"
                value={pasteAnchorCol}
                onChange={(e) => setPasteAnchorCol(Math.max(1, parseInt(e.target.value) || 1))}
                size="small"
                inputProps={{ min: 1 }}
              />
            </div>
          </>
        )}

        {blockError && !blockError.blockId && (
          <Typography color="error" variant="body2" mt={2}>
            {blockError.text}
          </Typography>
        )}

        <Typography variant="h4" mb={2} mt={2} color="text.secondary">
          Change part of the field id for all blocks
        </Typography>
        <div className="module-find-and-replace">
          <TextField
            label="Text to find"
            value={stringToFind}
            onChange={(e) => setStringToFind(e.target.value)}
            size="small"
          />
          <TextField
            label="Text to replace"
            value={stringToReplace}
            onChange={(e) => setStringToReplace(e.target.value)}
            size="small"
          />
          <Button
            onClick={() => findAndReplaceForAllFieldIds(stringToFind, stringToReplace)}
            disabled={!stringToFind || !stringToReplace}
            color="secondary"
          >
            Replace
          </Button>
        </div>

        <Typography variant="h4" mb={2} color="text.secondary">
          Manually change field id for each block
        </Typography>
        <div className="_d-grid-lg-gap">
          {blocksToPaste
            .filter((block) => block.oldFieldId || block.childBlocks)
            .map((block) => (
              <div className="_d-grid" key={block.id}>
                <div className="_d-flex-ali-center-jc-sb">
                  <Typography variant="h4">
                    {block.blockData.contentObject.title}{" "}
                    {block.isCreated ? <CheckIcon sx={{ color: "var(--c-success)" }} /> : ""}
                  </Typography>
                  {enableExistingBlocks && block.oldFieldId && (
                    <FormControlLabel
                      control={<Switch />}
                      label="Copy as existing"
                      checked={!!block.isPasteAsExisting}
                      onChange={() => handleIsPasteAsExistingChange(block.id, !block.isPasteAsExisting)}
                      labelPlacement="start"
                    />
                  )}
                </div>

                {block.oldFieldId && (
                  <TextField
                    label="Field id"
                    value={block.newFieldId}
                    onChange={(e) => handleNewFieldIdChange(block.id, e.currentTarget.value)}
                    size="small"
                    error={!!getFieldIdError(block.newFieldId) || blockError?.blockId === block.id}
                    helperText={
                      getFieldIdError(block.newFieldId) ||
                      (blockError?.blockId === block.id ? blockError.text : undefined)
                    }
                    disabled={block.isCreated || block.isPasteAsExisting}
                    InputProps={{
                      startAdornment: FieldIdPrefixes[block.blockData.contentType] ? (
                        <InputAdornment position="start">{FieldIdPrefixes[block.blockData.contentType]}_</InputAdornment>
                      ) : null,
                    }}
                  />
                )}

                {block.childBlocks?.filter((child) => child.oldFieldId).map((child) => (
                  <div className="_d-grid" key={child.oldId} style={{ paddingLeft: 16 }}>
                    <div className="_d-flex-ali-center-jc-sb">
                      <Typography variant="body2">
                        {child.blockData.contentObject.title}{" "}
                        {child.isCreated ? <CheckIcon sx={{ color: "var(--c-success)" }} /> : ""}
                      </Typography>
                      {child.oldFieldId && (
                        <FormControlLabel
                          control={<Switch size="small" />}
                          label="Copy as existing"
                          checked={!!child.isPasteAsExisting}
                          onChange={() =>
                            handleIsPasteAsExistingChildChange(block.id, child.oldId, !child.isPasteAsExisting)
                          }
                          labelPlacement="start"
                        />
                      )}
                    </div>
                    <TextField
                      label="Field id"
                      value={child.newFieldId}
                      onChange={(e) =>
                        handleNewChildFieldIdChange(block.id, child.oldId, e.currentTarget.value)
                      }
                      size="small"
                      error={!!validate.getFieldIdError(child.newFieldId)}
                      helperText={validate.getFieldIdError(child.newFieldId)}
                      disabled={child.isCreated || child.isPasteAsExisting}
                      InputProps={{
                        startAdornment: FieldIdPrefixes[child.blockData.contentType] ? (
                          <InputAdornment position="start">
                            {FieldIdPrefixes[child.blockData.contentType]}_
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </div>
                ))}

                {blockError?.blockId === block.id && (
                  <Button color="error" onClick={() => removeBlockToPaste(block.id)}>
                    Skip block
                  </Button>
                )}
              </div>
            ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={cancelPaste}>
          Cancel
        </Button>
        <Button disabled={!canCreate} onClick={createBlocks}>
          Create blocks
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface PasteBlocksDialogProps {
  copyAndPasteBlocksStore: CopyAndPasteBlocksStore;
  enableExistingBlocks?: boolean;
}
